import { useMemo } from "react"
import {
  Block,
  HeadingXXLarge,
  LabelLarge,
  LabelMedium,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import { bridge, IS_MEZO_MAINNET } from "#/shared/constants"
import { useEstimateBridgeAssetFees } from "#/shared/hooks/bridge"
import { BridgeAssetFormValue } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  convertFixedPoint,
  getCurrencySymbol,
} from "#/shared/utils"

import { useStepperModalContext } from "../StepperModal"

import BridgeSummaryList from "./BridgeSummaryList"

const ETHEREUM_CHAIN_LABEL = IS_MEZO_MAINNET ? "Ethereum" : "Sepolia"

type BridgeSummaryProps = { depositId?: number }

export default function BridgeSummary({ depositId }: BridgeSummaryProps) {
  const [css, theme] = useStyletron()

  const {
    values: { token, amount },
  } = useStepperModalContext<BridgeAssetFormValue>()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const { serviceFee } = useEstimateBridgeAssetFees(token, amount, depositId)

  const isERC20Token = token !== "BTC"

  const assetSummary: { label: string; amount: bigint; token: string }[] =
    useMemo(() => {
      const value = amount ?? 0n

      const bridgedAmount = isERC20Token
        ? value
        : value - convertFixedPoint(serviceFee, 18, 8)

      return [
        {
          label: `Bridge from ${
            isERC20Token ? ETHEREUM_CHAIN_LABEL : "Bitcoin"
          }`,
          amount: amount ?? 0n,
          token: getCurrencySymbol(token),
        },
        {
          label: `Get on Mezo ${IS_MEZO_MAINNET ? "Mainnet" : "Testnet"}`,
          amount: bridgedAmount,
          token: bridge.BRIDGEABLE_TOKENS[token].bridgedTokenSymbol,
        },
      ]
    }, [amount, isERC20Token, serviceFee, token])

  return (
    <Block
      className={css({
        height: "fit-content",
        display: "flex",
        flexDirection: "column",
        gap: theme.sizing.scale900,
      })}
    >
      <Block
        className={css({
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: theme.sizing.scale800,
        })}
      >
        {assetSummary.map((summary) => (
          <Block key={summary.label} padding={`0 ${theme.sizing.scale500}`}>
            <LabelMedium marginBottom={theme.sizing.scale300}>
              {summary.label}
            </LabelMedium>
            <Block
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: `${theme.sizing.scale500} ${theme.sizing.scale600}`,
                borderRadius: theme.borders.radius500,
                ...theme.borders.border500,
              })}
            >
              <HeadingXXLarge
                className={css({
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                })}
              >
                {bigIntToHumanReadableFormat(
                  summary.amount,
                  tokenDecimals,
                  tokenDecimals,
                )}
              </HeadingXXLarge>
              <LabelLarge marginLeft={theme.sizing.scale100}>
                {summary.token}
              </LabelLarge>
            </Block>
          </Block>
        ))}
      </Block>

      <BridgeSummaryList amount={amount} token={token} />
    </Block>
  )
}
